import { useDroppable, useDndContext } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Anchor, ChevronDown, ChevronUp, ClipboardPaste, GripVertical, Info, Link2, MousePointerSquareDashed, Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useStandInPickHandler , useAnswerRefScope } from './answer-ref-context';
import { ANSWERABLE_BLOCK_TYPES, AVAILABLE_BLOCK_TYPES, BLOCK_META, BlocksIcon, MOTHBALLED_BLOCK_TYPES } from './block-library';
import CompassBlock from './compass-block';
import DataVizBlock from './dataviz-block';
import DropdownBlock from './dropdown-block';
import { BlockIdProvider } from './fired-states';
import FitbBlock from './fitb-block';
import GpsBlock from './gps-block';
import ImageBlock from './image-block';
import KeywordBlock from './keyword-block';
import MCQBlock from './mcq-block';
import NumericalBlock from './numerical-block';
import OrderingBlock from './ordering-block';
import { QuickAddBar } from './quick-add-bar';
import RotationBlock from './rotation-block';
import ScaleBlock from './scale-block';
import SliderBlock from './slider-block';
import SortBlock from './sort-block';
import SoundBlock from './sound-block';
import TextBlock from './text-block';
import TiltBlock from './tilt-block';
import { PHONE_QUERY } from './use-viewport';

import type { BlockType } from './block-library';
import type { Block } from '@/types/experience';

import { useLatchedEffects } from '@/components/preview-session-context';
import { Button } from '@/components/ui/button';
import { drivenLinkLabel } from '@/lib/driven-links';
import { applyEffectsToBlock } from '@/lib/flow-effects';
import { scenarioLinkLabel } from '@/lib/scenario-links';


// ─── Shared embedded-panel renderer ──────────────────────────────────────────
// Single source of truth for block type → component. Both the task list and
// the right-side editing panel use this. Adding a new block type: add ONE line
// here (and import the component above).

export interface EmbeddedBlockBaseProps {
  block: Block;
  isPreview: false;
  embedded: true;
  experienceId: string;
  taskId: string;
  taskTitle?: string;
  blocks: Block[];
  onUpdate: (data: Record<string, unknown>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onClose: () => void;
  onRename: (name: string) => void;
}

export interface EmbeddedBlockExtras {
  initialIsEditingFeedback?: boolean;
  onFeedbackModeChange?: (v: boolean) => void;
  siblingGpsTargets?: { blockId: string; taskTitle: string; lat: number; lng: number }[];
}

export function renderEmbeddedBlock(
  selected: Block,
  baseProps: EmbeddedBlockBaseProps,
  panelKey: string,
  extras: EmbeddedBlockExtras = {},
): React.ReactNode {
  switch (selected.type) {
    case 'text':      return <TextBlock      key={panelKey} {...baseProps} />;
    case 'image':     return <ImageBlock     key={panelKey} {...baseProps} />;
    case 'sound':     return <SoundBlock     key={panelKey} {...baseProps} />;
    case 'dataviz':   return <DataVizBlock   key={panelKey} {...baseProps} />;
    case 'mcq':       return <MCQBlock       key={panelKey} {...baseProps} initialIsEditingFeedback={extras.initialIsEditingFeedback ?? false} onFeedbackModeChange={extras.onFeedbackModeChange} />;
    case 'numerical': return <NumericalBlock key={panelKey} {...baseProps} />;
    case 'keyword':   return <KeywordBlock   key={panelKey} {...baseProps} initialIsEditingFeedback={extras.initialIsEditingFeedback ?? false} onFeedbackModeChange={extras.onFeedbackModeChange} />;
    case 'fitb':      return <FitbBlock      key={panelKey} {...baseProps} initialIsEditingFeedback={extras.initialIsEditingFeedback ?? false} onFeedbackModeChange={extras.onFeedbackModeChange} />;
    case 'dropdown':  return <DropdownBlock  key={panelKey} {...baseProps} />;
    case 'slider':    return <SliderBlock    key={panelKey} {...baseProps} initialIsEditingFeedback={extras.initialIsEditingFeedback ?? false} onFeedbackModeChange={extras.onFeedbackModeChange} />;
    case 'gps':       return <GpsBlock       key={panelKey} {...baseProps} initialIsEditingFeedback={extras.initialIsEditingFeedback ?? false} onFeedbackModeChange={extras.onFeedbackModeChange} siblingGpsTargets={extras.siblingGpsTargets} />;
    case 'ordering':  return <OrderingBlock  key={panelKey} {...baseProps} initialIsEditingFeedback={extras.initialIsEditingFeedback ?? false} onFeedbackModeChange={extras.onFeedbackModeChange} />;
    case 'sort':      return <SortBlock      key={panelKey} {...baseProps} initialIsEditingFeedback={extras.initialIsEditingFeedback ?? false} onFeedbackModeChange={extras.onFeedbackModeChange} />;
    case 'scale':     return <ScaleBlock     key={panelKey} {...baseProps} initialIsEditingFeedback={extras.initialIsEditingFeedback ?? false} onFeedbackModeChange={extras.onFeedbackModeChange} />;
    case 'compass':   return <CompassBlock   key={panelKey} {...baseProps} initialIsEditingFeedback={extras.initialIsEditingFeedback ?? false} onFeedbackModeChange={extras.onFeedbackModeChange} />;
    case 'rotation':     return <RotationBlock     key={panelKey} {...baseProps} initialIsEditingFeedback={extras.initialIsEditingFeedback ?? false} onFeedbackModeChange={extras.onFeedbackModeChange} />;
    case 'tilt':      return <TiltBlock      key={panelKey} {...baseProps} initialIsEditingFeedback={extras.initialIsEditingFeedback ?? false} onFeedbackModeChange={extras.onFeedbackModeChange} />;
    default:          return null;
  }
}

interface TaskContentProps {
  /** The scan root for Preview's accessibility reports — the previewed task and nothing around it,
   * so a report can never reach the panel or bar beside it. */
  rootRef?: React.Ref<HTMLDivElement>;
  /** Preview's accessibility review transforms — a colour-vision filter and/or 200% zoom. Undefined
   * outside Preview. A style rather than a class because `zoom` has no Tailwind utility. */
  viewStyle?: React.CSSProperties;
  title: string;
  experienceId: string;
  taskId: string;
  /**
   * The task whose blocks these ARE, which is not always the task in the URL.
   *
   * `taskId` changes the moment a stepper arrow is clicked; `blocks` changes a commit later, when
   * the page applies the destination. For that one commit the two disagree, and the presence group
   * below has to follow the blocks rather than the URL — see its own note.
   */
  shownTaskId?: string;
  blocks: Block[];
  isPreview: boolean;
  /** Bumped once per click of Preview's single bottom Submit — each block reacts to a CHANGE in
   * this value (not its raw truthiness) to evaluate its own current Preview answer, replacing the
   * old per-block Submit button. See task-workspace-page.tsx's own doc comment on the state. */
  previewSubmitSignal: number;
  /** Bumped by Preview's Reset button; part of each block's key so they remount clean. */
  previewResetKey?: number;
  /** Which past visit is being reviewed, or null when the student is answering live. */
  previewReviewIndex?: number | null;
  /**
   * The task is a RECORD now, not a form — a completed attempt, or an earlier one being
   * reviewed. Blocks stop taking input and the page keeps scrolling.
   *
   * It has to be applied in HERE rather than around `TaskContent`, and that is the whole
   * reason the prop exists. Outside, it lands on an ancestor of the scroll container: an
   * overlay there has no scrollable ancestor of its own, and `pointer-events: none` there
   * covers the scroller itself, so a wheel has nothing to act on. Either way scrolling dies —
   * reported as "i was scrolled to the top of the exercise and scrolling then became
   * disabled", which is the worst possible moment for it, since the state exists so a student
   * can go back and read feedback that is below the fold.
   *
   * Applied to the content INSIDE the scroller, the scroller keeps its pointer events and the
   * wheel still reaches it. CSS only, so unlike `inert` the task stays in the accessibility
   * tree — the constraint that ruled `inert` out and still rules.
   */
  reviewLocked?: boolean;
  selectedBlockId?: string | null;
  highlightedBlockId?: string | null;
  /**
   * WHAT INSPECT IS SAYING ABOUT RECALLS, as a mark per block.
   *
   * 'waiting' is the standing one: this block quotes an answer no run has produced and no
   * teacher has supplied, so it renders a dimmed name until one of those happens. 'lit' is the
   * pointer — the teacher is hovering that reference's row in the panel and asking what it
   * feeds — and so wins where a block is both, since a pointer that vanished on the blocks it
   * was pointing at would be answering the opposite question.
   *
   * A function rather than a set so the caller keeps the choice of what counts; Preview passes
   * one and every other surface passes nothing, which is the whole of the gating.
   */
  recallMark?: (blockId: string) => 'waiting' | 'lit' | null;
  mobileEditingId?: string | null;
  phoneBlockMountKey?: number;
  onPhoneEditOpen?: (id: string) => void;
  onPhoneEditClose?: () => void;
  onShowLibrary: () => void;
  onSelectBlock?: (id: string) => void;
  onRemoveBlock: (id: string) => void;
  onUpdateBlock: (id: string, data: Record<string, unknown>) => void;
  onDropBlock: (type: BlockType) => void;
  onMoveBlock: (from: number, to: number) => void;
  onDuplicateBlock: (id: string) => void;
  /** What the clipboard is holding, e.g. "Numerical 1" — undefined when it holds nothing, which is
   * what hides the paste affordance entirely. Naming it is also the standing confirmation that a
   * copy worked, since copying changes nothing else on the page. */
  clipboardLabel?: string;
  /** Paste after this index. `-1` places before the first block. */
  onPlaceAt?: (insertAfterIndex: number) => void;
  onRenameBlock: (id: string, name: string) => void;
  siblingGpsTargets?: { blockId: string; taskTitle: string; lat: number; lng: number }[];
  /**
   * Overlays each block's own name ("Numerical 1") on its canvas card.
   *
   * It was shown only while the Flow panel was open, on the reasoning that correlating "which
   * physical block is this condition talking about" was the only time the instance name
   * mattered — the collapsed card otherwise shows its generic type label (see
   * collapsed-input-block.tsx's `titleLabel`), which cannot tell three MCQs apart.
   *
   * Never in Preview, whatever this says — see `BlockRow`, which checks `isPreview` itself.
   *
   * Answer Recall made that false. A reference is read, written and repaired by block NAME
   * everywhere it appears — in the picker, in the panel's address lines, in the warning that
   * sends you to the block that recalls an answer — and none of that is Flow. Arriving at a
   * card you were sent to and having to open it to confirm it is the one named is the gap.
   * So the name is on the card whenever the canvas is.
   *
   * Still `aria-hidden`: the card's accessible name is composed by each block and does not yet
   * carry the instance name, so an un-hidden badge would announce a bare "MCQ 1" beside a label
   * that never mentions it. That half belongs to the planned editor-wide name sweep, where it
   * is one rule applied to fourteen blocks rather than a fifteenth exception.
   */
  showBlockNameBadge?: boolean;
}

const FIXED_QUICK_ADD: BlockType[] = ['text', 'image'];

function getQuickAddTypes(blocks: Block[]): BlockType[] {
  // Mothballed types stay in AVAILABLE_BLOCK_TYPES because existing blocks still render, but
  // quick-add CREATES, so they are excluded here as they are in the library.
  const pool = AVAILABLE_BLOCK_TYPES.filter(
    t => !FIXED_QUICK_ADD.includes(t) && !MOTHBALLED_BLOCK_TYPES.includes(t),
  );
  const counts: Record<string, number> = Object.fromEntries(pool.map(t => [t, 0]));
  blocks.forEach(b => { if (b.type in counts) counts[b.type] = (counts[b.type] ?? 0) + 1; });
  const sorted = [...pool].sort((a, b) => (counts[b] ?? 0) - (counts[a] ?? 0));
  return [...FIXED_QUICK_ADD, ...sorted.slice(0, 3)];
}

export function useIsMobile() {
  const query = PHONE_QUERY;
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

export function useIsMobilePortrait() {
  const mql = '(max-width: 639px) and (orientation: portrait)';
  const [is, setIs] = useState(() => window.matchMedia(mql).matches);
  useEffect(() => {
    const mq = window.matchMedia(mql);
    const handler = (e: MediaQueryListEvent) => setIs(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return is;
}

/**
 * The space between two blocks, when there is something to paste into it.
 *
 * ONE button that grows into a chip, rather than a chip overlaid on a 16px gap. The overlay had to
 * spill onto the cards above and below because it was bigger than the space it sat in, and it was
 * `opacity-0` until hovered — invisible and still tappable on a touch screen.
 *
 * Correct on all three inputs from a single control, by reading what the gesture already tells us:
 *
 *   - mouse: hovering has already grown it, so the click that follows pastes — one click.
 *   - touch: nothing hovers, so the first tap only grows it and the second, now aimed at a chip
 *     the teacher can see, pastes. A tap between blocks can never paste something by surprise.
 *   - keyboard: `detail === 0` marks a key press, which pastes outright; focus grows it first so
 *     the target is visible while it is the focused thing.
 *
 * IDENTICAL GEOMETRY open and closed on touch — same height, same width — so opening it changes
 * only colours. It used to be `transition-all` across a changing width, margin and shadow, which
 * animates layout on every frame and visibly lagged the tap that triggered it. On a pointer the
 * box does still grow, but `transition-colors` means the size snaps and only the fill fades, so
 * nothing is tweening a reflow there either.
 */
function PasteGap({ label, onPaste, open, onOpen, onClose }: {
  label: string;
  onPaste: () => void;
  /**
   * Owned by the list, not by each gap.
   *
   * Held locally, every gap opened independently: on touch there is no `mouseleave`, so tapping a
   * second gap left the first one still standing open, and a task could end up with three chips
   * showing at once. One owner means opening any gap necessarily closes the last.
   */
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  /** Whether it was already open when this press STARTED — the difference between a touch
   *  revealing it and a mouse clicking the chip it has been hovering. */
  const openAtPressStart = useRef(false);

  return (
    <div
      data-paste-gap=''
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
      className='flex items-center justify-center'
    >
      <button
        type='button'
        onPointerDown={(e) => { e.stopPropagation(); openAtPressStart.current = open; onOpen(); }}
        onClick={(e) => { e.stopPropagation(); if (e.detail === 0 || openAtPressStart.current) onPaste(); }}
        onFocus={onOpen}
        onBlur={onClose}
        aria-label={label}
        /*
         * `h-11` on touch, `lg:` smaller — the same mobile-first sizing the move buttons on the
         * rail use. It matters most in the CLOSED state, which is the target the first tap has to
         * land on: at the 8px this started as, the hint was a fifth of the app's own touch target
         * and the gesture that opens the chip was the hardest part of the interaction.
         *
         * The button is tall while the visible hint stays a thin line, so the space a finger gets
         * is not the space the eye sees.
         */
        /*
         * The BUTTON carries the hit area; the span inside carries the look. Keeping them separate
         * is what lets the chip be smaller than the space it sits in — at `h-11` it filled the gap
         * edge to edge and butted against the cards above and below the moment it opened.
         */
        className={`flex items-center justify-center transition-colors duration-150 focus-visible:[outline:2px_auto] ${
          open ? 'h-11 w-40 lg:h-10 lg:w-auto' : 'h-11 w-40 lg:h-4 lg:w-24'
        }`}
      >
        {open ? (
          <span className='flex h-8 items-center justify-center gap-1.5 rounded-full border border-ws-primary bg-ws-surface px-4 text-sm font-medium text-ws-primary shadow-md'>
            <ClipboardPaste aria-hidden='true' className='h-4 w-4 shrink-0' />
            {t('task.paste.label')}
          </span>
        ) : (
          <span aria-hidden='true' className='h-2 w-24 rounded-full border border-dashed border-ws-border' />
        )}
      </button>
    </div>
  );
}

function DroppableGap({ id }: { id: string }) {
  const { setNodeRef, isOver, active } = useDroppable({ id });
  const isLibraryDrag = active?.data.current?.source === 'library';
  const show = isOver && isLibraryDrag;
  return (
    <div
      ref={setNodeRef}
      /* The same height as a closed PasteGap, deliberately. It reads better — the blocks need the
         separation to be legible as separate questions — and it means copying something changes no
         layout at all: the gap a paste target appears in is the gap that was already there. */
      className={`relative flex items-center justify-center transition-all duration-150 ${isLibraryDrag ? (show ? 'h-12 lg:h-10' : 'h-11 lg:h-7') : 'h-11 lg:h-4'}`}
    >
      {show && <div className='absolute inset-x-0 h-0.5 rounded-full bg-ws-primary' />}
    </div>
  );
}

interface SortableBlockRowProps {
  block: Block;
  idx: number;
  blocksLength: number;
  isPreview: boolean;
  isHighlighted: boolean;
  recallMark?: 'waiting' | 'lit' | null;
  isMain: boolean;
  showNameBadge: boolean;
  /**
   * §8.3's group markers — one per relationship this block is in, outermost first.
   *
   * Two, not one: the axes are mutually exclusive as VERSION owners and the relationships are
   * not, so a scenario follower can also drive a later task's question. `kind` picks the
   * glyph, and the glyph is the whole vocabulary — see `drivenLinkLabel`.
   */
  linkTags: LinkTag[];
  /** Travel to the other end of a cross-task tag. Absent where the surface cannot navigate. */
  onOpenLink?: (taskId: string, blockId: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  /** A paste target below this block, present only while the clipboard holds something. */
  pasteAfter?: { label: string; onPaste: () => void; open: boolean; onOpen: () => void; onClose: () => void };
  upRef: (el: HTMLButtonElement | null) => void;
  downRef: (el: HTMLButtonElement | null) => void;
  children: React.ReactNode;
}

function SortableBlockRow({
  block, idx, blocksLength, isPreview, isHighlighted, recallMark, isMain, showNameBadge, linkTags, onOpenLink,
  onMoveUp, onMoveDown, pasteAfter, upRef, downRef, children,
}: SortableBlockRowProps) {
  const { t } = useTranslation();
  const { listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });

  return (
    <>
      <div
        ref={setNodeRef}
        style={{ transform: CSS.Transform.toString(transform), transition }}
        className={`group flex items-center gap-1 rounded-xl transition-opacity duration-700 ${
          isDragging ? 'opacity-40' : 'opacity-100'
        }`}
      >
        {!isPreview && (
          <div className='flex shrink-0 flex-col items-center self-stretch justify-center gap-0.5'>
            <button
              ref={upRef}
              onPointerDown={(e) => { if (e.button !== 0) return; e.stopPropagation(); onMoveUp(); }}
              onClick={(e) => { e.stopPropagation(); if (e.detail === 0) onMoveUp(); }}
              disabled={idx === 0}
              className='flex h-11 w-8 items-center justify-center rounded-md hover:bg-ws-hover disabled:opacity-20 lg:h-7 lg:w-7 [&:focus:not(:focus-visible)]:outline-none'
              aria-label={t('task.moveUp')}
            >
              <ChevronUp className='h-4 w-4 text-ws-muted' />
            </button>
            <div
              {...listeners}
              tabIndex={-1}
              aria-hidden='true'
              className='flex cursor-grab active:cursor-grabbing touch-none rounded'
            >
              <GripVertical className='h-4 w-4 text-ws-muted' />
            </div>
            <button
              ref={downRef}
              onPointerDown={(e) => { if (e.button !== 0) return; e.stopPropagation(); onMoveDown(); }}
              onClick={(e) => { e.stopPropagation(); if (e.detail === 0) onMoveDown(); }}
              disabled={idx === blocksLength - 1}
              className='flex h-11 w-8 items-center justify-center rounded-md hover:bg-ws-hover disabled:opacity-20 lg:h-7 lg:w-7 [&:focus:not(:focus-visible)]:outline-none'
              aria-label={t('task.moveDown')}
            >
              <ChevronDown className='h-4 w-4 text-ws-muted' />
            </button>
            {!isMain && (
              <div className='group/debug relative'>
                <Info className='h-3.5 w-3.5 text-ws-muted opacity-40 hover:opacity-100 cursor-default' />
                <div className='pointer-events-none absolute left-5 top-0 z-50 hidden w-56 rounded-md border border-ws-border bg-ws-surface p-2.5 shadow-md group-hover/debug:block'>
                  <p className='text-[10px] font-medium uppercase tracking-wide text-ws-muted mb-1.5'>{t('experience.debug.title')}</p>
                  <div className='space-y-1'>
                    <div><span className='text-[10px] text-ws-muted'>{t('experience.debug.id')}</span><p className='font-mono text-[11px] text-ws-text break-all'>{block.id}</p></div>
                    <div><span className='text-[10px] text-ws-muted'>{t('experience.debug.heritage')}</span><p className='font-mono text-[11px] text-ws-text break-all'>{block.heritageId ?? t('experience.debug.original')}</p></div>
                    <div><span className='text-[10px] text-ws-muted'>{t('experience.debug.author')}</span><p className='font-mono text-[11px] text-ws-text break-all'>{block.authorId ?? '—'}</p></div>
                    <div><span className='text-[10px] text-ws-muted'>{t('experience.debug.type')}</span><p className='font-mono text-[11px] text-ws-text'>{block.type}</p></div>
                    <div><span className='text-[10px] text-ws-muted'>{t('experience.debug.created')}</span><p className='font-mono text-[11px] text-ws-text'>{block.createdAt ? new Date(block.createdAt).toLocaleString() : '—'}</p></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {/* The pulse rings the CARD, not the row: the row also holds the reorder rail, so ringing it
            drew a box around the move buttons and the drag grip — chrome that did not change and is
            not what the pulse is pointing at. */}
        <div className={`relative min-w-0 flex-1 rounded-xl ${isHighlighted ? 'agavi-pulse' : ''} ${
          recallMark === 'lit' ? 'ws-recall-lit' : recallMark === 'waiting' ? 'ws-recall-waiting' : ''}`}>
          {children}
          {/* NEVER IN PREVIEW. The name is the teacher's handle on the block — what Flow
              conditions and Answer Recall addresses call it — and a student has no use for
              "MCQ 1" floating over their question. Checked here rather than left to the
              caller: `isPreview` is already the authoritative "this is the student's view"
              signal in this component, and a caller that forgot would leak editor furniture
              into the one surface that must not have any. */}
          {!isPreview && showNameBadge && block.name && (
            <span
              aria-hidden='true'
              className='pointer-events-none absolute -top-2 left-3 z-10 rounded-md bg-ws-primary dark:bg-ws-text-link px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-sm'
            >
              {block.name}
            </span>
          )}
          {/*
            * THE GROUP MARKER, mirroring the flow name badge on the other edge — §8.3.
            *
            * Right, because left is taken: that corner carries the block's own name, and two
            * pills in one corner would read as one run-on label.
            *
            * HIGHER than that badge, though, which is the one way they are not mirrored. The
            * left corner is empty; this one has the card's own copy/duplicate/delete cluster
            * sitting just inside it, and at the badge's own `-top-2` the pill covered the top
            * of it. The button still worked — the pill takes no pointer events — but a control
            * half-hidden behind a label reads as broken.
            *
            * Outlined rather than solid, for the same reason it is on the opposite edge: the
            * solid pill means "this block is called X", and a second solid pill would claim to
            * be another name. An outline says annotation.
            */}
          {linkTags.length > 0 && (
            <span className='absolute -top-4 right-3 z-10 flex items-center gap-1'>
              {linkTags.map(tag => {
                /*
                 * A TAG WITH SOMEWHERE TO GO IS A BUTTON. Owner: "these are cross-task
                 * linkages ... i notice something is misbehaving, and i can just click that
                 * link to get jumped over to the exact origin". Only the driven end has a
                 * single destination; a scenario names a block in this same task, and a
                 * DRIVER is read by however many blocks choose to.
                 */
                const go = tag.goTo && onOpenLink;
                const Tag = go ? 'button' : 'span';
                return (
                <Tag
                  key={tag.kind}
                  {...(go
                    ? { type: 'button' as const,
                      onClick: (e: React.MouseEvent) => {
                        /* The card under it opens the editor on click; this press is spent. */
                        e.stopPropagation();
                        onOpenLink!(tag.goTo!.taskId, tag.goTo!.blockId);
                      } }
                    : { 'aria-hidden': undefined })}
                  className={`flex items-center gap-1 rounded-md border border-ws-primary bg-ws-surface px-1.5 py-0.5 text-[10px] font-semibold text-ws-primary shadow-sm dark:border-ws-text-link dark:text-ws-text-link${
                    go ? ' cursor-pointer hover:bg-ws-hover' : ' pointer-events-none'}`}
                >
                  {/* ANCHOR for a scenario, LINK for a driver, and the pair carries the
                      cross-task distinction on its own: a scenario link names a block in this
                      same task and cannot do otherwise, while a driver is always in an earlier
                      one. A third signal for same-task-versus-cross would say nothing the
                      glyph has not. */}
                  {tag.kind === 'scenario'
                    ? <Anchor aria-hidden='true' className='h-3 w-3 shrink-0' />
                    : <Link2 aria-hidden='true' className='h-3 w-3 shrink-0' />}
                  <span aria-hidden='true' className='max-w-[9rem] truncate'>{tag.text}</span>
                  {/* The relationship said in full, since the glyph cannot be read aloud and
                      the name alone would announce as a stray block name. */}
                  <span className='sr-only'>{tag.a11y}</span>
                </Tag>
                );
              })}
            </span>
          )}
        </div>
      </div>

      {!isPreview && pasteAfter && <PasteGap {...pasteAfter} />}
      {!isPreview && !pasteAfter && idx < blocksLength - 1 && (
        <DroppableGap id={`gap-${idx}`} />
      )}
    </>
  );
}

/**
 * One tag, or none — the shape `linkTags` is built from.
 *
 * A tiny wrapper rather than two `&&`s at the call site: the list is spread, so a null has to
 * become an empty array somewhere, and doing it inline twice is where the second one comes to
 * be written differently from the first.
 */
function wrapTag(
  kind: 'scenario' | 'driven',
  label: { text: string; a11y: string; goTo?: { taskId: string; blockId: string } } | null,
): LinkTag[] {
  return label ? [{ kind, ...label }] : [];
}

interface LinkTag {
  kind: 'scenario' | 'driven';
  text: string;
  a11y: string;
  /** Where to go, for the end of a cross-task link that has exactly one destination. */
  goTo?: { taskId: string; blockId: string };
}

export default function TaskContent({
  rootRef,
  viewStyle,
  title,
  experienceId,
  taskId,
  shownTaskId,
  blocks,
  isPreview,
  previewSubmitSignal,
  previewResetKey = 0,
  previewReviewIndex = null,
  reviewLocked = false,
  selectedBlockId,
  highlightedBlockId,
  recallMark,
  mobileEditingId = null,
  phoneBlockMountKey,
  onPhoneEditOpen,
  onPhoneEditClose,
  onShowLibrary,
  onSelectBlock,
  onRemoveBlock,
  onUpdateBlock,
  onDropBlock,
  onMoveBlock,
  onDuplicateBlock,
  clipboardLabel,
  onPlaceAt,
  onRenameBlock,
  siblingGpsTargets,
  showBlockNameBadge = false,
}: TaskContentProps) {
  const isMobile = useIsMobile();
  const isMobilePortrait = useIsMobilePortrait();
  // Someone who has asked the OS for less motion gets the layout change instantly, not animated.
  const reduceMotion = useReducedMotion();
  const { active: dndActive } = useDndContext();
  const isLibraryDragging = dndActive?.data.current?.source === 'library';
  const [announcement, setAnnouncement] = useState('');
  // Preview's second way to fill a reference — see the listener on the scroll container below.
  const onPickStandIn = useStandInPickHandler();

  /**
   * Which paste gap is open, by the index it would paste after (`-1` = before the first block).
   *
   * One owner for the whole list, so opening any gap closes the one before it — without this each
   * gap kept its own flag and, with no `mouseleave` on a touch screen, tapping a second gap simply
   * left the first standing open.
   */
  const [openGap, setOpenGap] = useState<number | null>(null);

  // A tap anywhere that is not a gap closes the open one. Touch has no pointer to move away.
  useEffect(() => {
    if (openGap === null) return;
    const away = (e: PointerEvent) => {
      if (!(e.target as HTMLElement | null)?.closest?.('[data-paste-gap]')) setOpenGap(null);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [openGap]);
  const [lastMoved, setLastMoved] = useState<{ id: string; direction: 'up' | 'down' } | null>(null);
  const upButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const downButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const { t } = useTranslation();
  /* Both ends of the driven axis need the whole experience: the driver is in an earlier task
     and everything it drives is in a later one. Read once, not per card. */
  const refScope = useAnswerRefScope();

  function moveBlock(id: string, direction: 'up' | 'down') {
    const idx = blocks.findIndex((b) => b.id === id);
    if (idx === -1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= blocks.length) return;
    onMoveBlock(idx, swapIdx);
    setAnnouncement(t(direction === 'up' ? 'task.movedUp' : 'task.movedDown', { position: swapIdx + 1, total: blocks.length }));
    setLastMoved({ id, direction });
  }

  useEffect(() => {
    if (!lastMoved) return;
    // The button just clicked can end up disabled at its new boundary position (e.g. moving a
    // block down into the last slot disables its own "down" button) — a disabled element can't
    // take focus, so fall back to the opposite-direction button on the same row.
    const primary = lastMoved.direction === 'up' ? upButtonRefs.current.get(lastMoved.id) : downButtonRefs.current.get(lastMoved.id);
    const fallback = lastMoved.direction === 'up' ? downButtonRefs.current.get(lastMoved.id) : upButtonRefs.current.get(lastMoved.id);
    (primary && !primary.disabled ? primary : fallback)?.focus();
    setLastMoved(null);
  }, [blocks, lastMoved]);

  /** Latched for the whole run, so a block a Change opened stays open on the next attempt. */
  const sessionEffects = useLatchedEffects();

function renderBlock(raw: Block, isMain: boolean) {
    // What a Change has turned on so far this run, laid over what the teacher authored. Only in
    // Preview, and never written back: the stored task must not drift because a student needed
    // three goes at it. Outside a run this is the same object, so nothing re-renders.
    const block = isPreview ? applyEffectsToBlock(raw, sessionEffects) : raw;
    const isPhoneEditing = isMobile && mobileEditingId === block.id;
    const onSelect = !isMobile && onSelectBlock
      ? () => onSelectBlock(block.id)
      : isMobile ? () => onPhoneEditOpen?.(block.id) : undefined;
    const isSelected = !isPreview && selectedBlockId === block.id;
    const mobileIsEditing = isMobile ? mobileEditingId === block.id : undefined;
    const onClose = isMobile ? () => onPhoneEditClose?.() : undefined;
    // Remount the active phone-edited block when phoneBlockMountKey changes (after undo/redo)
    // so the component re-initializes its internal state from the restored block.data
    const blockKey = isPhoneEditing && phoneBlockMountKey !== undefined
      ? `${block.id}-p${phoneBlockMountKey}`
      // Preview's Reset works by remounting: a block's answer, its submitted snapshot and its
      // continuous flag are all internal, so this is the one lever that clears every block at once
      // without each of them growing a reset API of its own.
      // `previewReviewIndex` joins the key so stepping through review remounts every block:
      // they read their restored answer in a `useState` initializer, which only runs at mount.
      : isPreview ? `${block.id}-r${previewResetKey}-v${previewReviewIndex ?? 'live'}`
      : block.id;
    const sharedProps = {
      isPreview, previewSubmitSignal, onSelect, isSelected, isDragOver: false,
      mobileIsEditing, onClose, isMain, experienceId, taskId, taskTitle: title, blocks, siblingGpsTargets,
      onUpdate: (d: Record<string, unknown>) => onUpdateBlock(block.id, d),
      onRemove: () => { onRemoveBlock(block.id); setAnnouncement(t('task.a11y.blockDeleted')); },
      onDuplicate: () => { onDuplicateBlock(block.id); setAnnouncement(t('task.a11y.blockDuplicated')); },
      onRename: (name: string) => onRenameBlock(block.id, name),
    };
    if (block.type === 'text') return <TextBlock key={blockKey} block={block} {...sharedProps} />;
    if (block.type === 'image') return <ImageBlock key={blockKey} block={block} {...sharedProps} />;
    if (block.type === 'sound') return <SoundBlock key={blockKey} block={block} {...sharedProps} />;
    if (block.type === 'dataviz') return <DataVizBlock key={blockKey} block={block} {...sharedProps} />;
    if (block.type === 'mcq') return <MCQBlock key={blockKey} block={block} {...sharedProps} />;
    if (block.type === 'numerical') return <NumericalBlock key={blockKey} block={block} {...sharedProps} />;
    if (block.type === 'keyword') return <KeywordBlock key={blockKey} block={block} {...sharedProps} />;
    if (block.type === 'fitb') return <FitbBlock key={blockKey} block={block} {...sharedProps} />;
    if (block.type === 'dropdown') return <DropdownBlock key={blockKey} block={block} {...sharedProps} />;
    if (block.type === 'slider') return <SliderBlock key={blockKey} block={block} {...sharedProps} />;
    if (block.type === 'gps') return <GpsBlock key={blockKey} block={block} {...sharedProps} />;
    if (block.type === 'ordering') return <OrderingBlock key={blockKey} block={block} {...sharedProps} />;
    if (block.type === 'sort') return <SortBlock key={blockKey} block={block} {...sharedProps} />;
    if (block.type === 'scale') return <ScaleBlock key={blockKey} block={block} {...sharedProps} />;
    if (block.type === 'compass') return <CompassBlock key={blockKey} block={block} {...sharedProps} />;
    if (block.type === 'rotation') return <RotationBlock key={blockKey} block={block} {...sharedProps} />;
    if (block.type === 'tilt') return <TiltBlock key={blockKey} block={block} {...sharedProps} />;
    return (
      <div className='group relative rounded-xl border border-ws-border bg-ws-surface p-4'>
        <span className='text-sm text-ws-muted'>{block.type}</span>
        {!isPreview && (
          <button onClick={() => onRemoveBlock(block.id)} className='absolute right-3 top-3 hidden h-7 w-7 items-center justify-center rounded-md text-ws-muted hover:bg-ws-hover hover:text-red-500 group-hover:flex' aria-label={t('task.removeBlock')}>
            <Trash2 className='h-4 w-4' />
          </button>
        )}
      </div>
    );
  }

  return (
    // `viewStyle` carries Preview's accessibility review transforms (colour-vision filter,
    // 200% zoom). Applied HERE rather than to the page's flex row so it reaches the task and not
    // the panel or bar beside it — simulating the app's own chrome is noise a teacher cannot act
    // on, and a tinted control panel makes the active mode harder to read.
    <div ref={rootRef} style={viewStyle} className='flex flex-1 flex-col w-full bg-gray-50 dark:bg-ws-bg overflow-hidden'>
      <div aria-live='polite' aria-atomic='true' className='sr-only'>{announcement}</div>

      {/* Scrollable block list. `data-task-scroll` so Preview can send a student back to the top of
          the task after the summary dialog without having to guess which div scrolls. */}
      {/*
        * SETTING A RECALLED ANSWER BY HAND, caught here for the whole task.
        *
        * One delegated listener rather than one per block: a reference is rendered into prose
        * as an html string, wherever that prose happens to be, so there is nothing to attach a
        * handler to at the point it is drawn. Capture, because the card and the prose beneath
        * both spend clicks of their own on the way up — the same arrangement the editor's
        * `useRecallPickHandler` needed, for the same reason.
        *
        * Does nothing outside a run, and nothing on a click that did not land on a reference.
        */}
      <div data-task-scroll='' onClickCapture={onPickStandIn} className={`flex-1 min-h-0 ${isLibraryDragging ? 'overflow-y-hidden' : 'overflow-y-auto'}`}>
        {/* `relative` for the badge layer below, and for nothing else. Safe because every block
            that positions something absolutely already establishes its own containing block (a
            dropdown's popup, an image's overlay) — none of them reach past their own card. */}
        {/* `px-5` on a PHONE IN PREVIEW, where the task column now runs to the screen edge — the
            workspace gutter that used to hold the cards off the glass is gone, deliberately, so that
            the dim and the bottom bar can be flush. The cards still need to be off it, so the inset
            moves in here where it belongs to the content rather than to the frame. Symmetric, unlike
            the editor's `pl-1 pr-3`, which leaves room on the right for the block toolbar that
            Preview does not render. */}
        {/* `pt-5`, not `pt-2`: the scroll parent is `overflow-hidden`, and the FIRST card's
            scenario pill hangs 16px above it (`-top-4`). 8px of padding was enough for the name
            badge at `-top-2`, which is what sized this originally, and the pill added later was
            sheared in half at the top of the task. Padding rather than clipping-off, because the
            pill overhangs on purpose — it reads as attached to the card's edge. */}
        <div className={`relative mx-auto w-full min-h-full pt-5 pb-2 ${
          isMobile && isPreview ? 'px-5' : 'pl-1 pr-3'
        } ${reviewLocked ? 'cursor-not-allowed [&_*]:pointer-events-none' : ''}`}
        >
          {/*
            Where Preview's read-through hangs its numbered badges.

            Always rendered, even with the panel closed: it costs an empty div, and a permanent node
            is what makes a portal into it safe — React never has to reconcile a container it did
            not create.

            INSIDE the scrolled content on purpose. The badges used to be a `position: fixed` layer
            over the task, repositioned from viewport rects on every scroll event, which meant they
            lagged a frame behind the content, piled up against the top edge on the way out, and sat
            still through an overscroll bounce. Living in the content means CSS moves them: no
            scroll listener, no clamping, no z-index to coordinate against the bars outside.
          */}
          <div data-a11y-badge-layer='' className='pointer-events-none absolute inset-0 z-30' />
        {blocks.length === 0 ? (
          <div className={`flex w-full flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed py-18 transition-colors ${isLibraryDragging ? 'border-ws-primary bg-ws-surface text-ws-primary' : 'border-ws-border text-ws-muted'}`}>
            <MousePointerSquareDashed className='h-5 w-5' />
            {/* Desktop and tablet drag from the library sidebar. On PHONE there is no sidebar, so the
                empty state must itself open the library — otherwise a brand-new task has no way to add
                a first block, since the quick-add bar only appears once a block exists.
                Gated on `isMobile`, which is the app's own `PHONE_QUERY`, rather than on a Tailwind
                breakpoint: the two halves used to switch at `lg` while the sidebar switches at `md`, so
                a tablet between 768px and 1023px got BOTH — the sidebar right there, and a button
                offering to open it. One source of truth for "is this a phone" cannot drift like that. */}
            {isMobile
              ? (
                <Button onClick={onShowLibrary} icon={<Plus aria-hidden='true' />}>
                  {t('task.emptyAddBlock')}
                </Button>
              )
              : <span className='text-sm'>{t('task.emptyDragHint')}</span>}
            {/* An empty task has no gaps to reveal a chip in, and it is exactly the case copying a
                block into a NEW task produces, so the one boundary it does have gets its own. */}
            {clipboardLabel && onPlaceAt && (
              <PasteGap
                label={t('task.paste.atStart')}
                onPaste={() => { setOpenGap(null); onPlaceAt(-1); }}
                open={openGap === -1}
                onOpen={() => setOpenGap(-1)}
                onClose={() => setOpenGap(null)}
              />
            )}
          </div>
        ) : (
          <div className={`flex flex-col ${isPreview ? 'gap-3' : ''}`}>
            {/* No paste target ABOVE the first block. It put a 44px well between the toolbar and
                the first card for a position that is reachable anyway — paste anywhere and drag it
                up — and the top of the list is where empty space is most conspicuous. */}
            <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
              {/*
                * KEYED ON THE TASK, and it is a layout fix rather than an animation preference.
                *
                * `AnimatePresence` keeps a LEAVING child mounted until its exit fade finishes, so
                * it still takes up its full height for 200ms. That is right for a block the
                * teacher deleted. It is wrong for a hop between tasks: every block of the old task
                * leaves while every block of the new one arrives, so for 200ms the canvas holds
                * BOTH tasks stacked and then collapses — a visible stretch, and with image blocks
                * a scrollbar that appears and vanishes.
                *
                * The key unmounts the whole presence group, so the old task's cards go at once and
                * the new task's arrive with no enter animation (`initial={false}` applies again on
                * the remount). Exit stays exactly as it was WITHIN a task, which is the only place
                * it was ever meant to run.
                *
                * `shownTaskId`, NOT `taskId`, and the difference is the whole fix. The URL changes
                * a commit before the blocks do, so a key on `taskId` remounts the group while it
                * still holds the OLD blocks — which then exit from the fresh group exactly as
                * before. The key has to move in the same commit as `blocks`, and `shownTaskId` is
                * set where they are.
                *
                * This was invisible until the editor stopped tearing itself down between tasks:
                * the loading screen unmounted the list, which took the exiting nodes with it.
                */}
              <AnimatePresence initial={false} key={shownTaskId ?? taskId}>
                {blocks.map((block, idx) => {
                  /*
                   * UNIFORM DIMMING. A scenario leader used to be exempted to 0.75 so its prose
                   * stayed readable while you edited a follower; it did not work, and the fix
                   * was in the wrong place — see `scenarioLinkLabel`. Everything that is not
                   * being edited is equally not being edited.
                   */
                  const activeBlockId = isMobile ? mobileEditingId : selectedBlockId;
                  const dimTo = !activeBlockId || activeBlockId === block.id ? 1 : 0.4;
                  /** §8.3: a group is invisible unless each end says so — both groups. */
                  const linkTags = isPreview ? [] : [
                    ...wrapTag('scenario', scenarioLinkLabel(block, blocks, t)),
                    ...wrapTag('driven', drivenLinkLabel(
                      block, refScope?.tasks ?? [], refScope?.aliases ?? [], t)),
                  ];
                  const isMain = !isPreview && !isMobilePortrait;
                  /**
                   * PREVIEW ONLY — give the things a student ANSWERS a container, and leave what
                   * they read flowing on the page.
                   *
                   * Preview used to be a flat stream with a 12px gap and nothing else, so the
                   * strongly-coloured feedback that appears on Submit was the only structured thing
                   * on the page: it read as arriving from nowhere rather than belonging to the
                   * question above it. The feedback is already rendered INSIDE its block; this just
                   * makes that parentage visible, so submitting grows a block instead of inserting
                   * a stranger between blocks.
                   *
                   * The state goes on a left rail rather than the whole outline. A hairline in a
                   * status colour is a weak signal, and ringing a question in red to say "wrong"
                   * is louder than the message deserves. `has-[…]` reads the `data-eval` the shared
                   * FiredFeedback component stamps on each box, so no block has to report its
                   * result upward — and unlike keying off a background class, that attribute is a
                   * contract someone can find and keep. Only two rules, and they cannot both
                   * match: `correct` is terminal in every evaluator, so a block that fired it has
                   * no error feedback.
                   */
                  const answerable = isPreview && ANSWERABLE_BLOCK_TYPES.includes(block.type);
                  const previewShell = answerable
// Per-side widths, not the `border` shorthand: Tailwind emits border-width AFTER
                    // border-left-width, so the shorthand silently flattened the rail back to 1px.
                    ? 'rounded-xl border-y border-r border-l-[3px] border-ws-border/60 border-l-ws-border bg-ws-surface px-4 py-3 transition-colors has-[[data-eval=correct]]:border-l-ws-success-fg has-[[data-eval=incorrect]]:border-l-ws-error-fg'
                    : '';
                  return (
                  <motion.div
                    key={block.id}
                    id={`block-${block.id}`}
                    className={previewShell}
                    /* Grow rather than jump. One Submit resolves every block at once, so without
                     * this the whole page teleports; with it each block expands and the ones below
                     * slide down, which reads as marking. The small per-block delay walks the eye
                     * down the page instead of flashing everything simultaneously — capped, so a
                     * twenty-block task doesn't take a second to settle. */
                    layout={isPreview && !reduceMotion}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: dimTo }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, layout: { duration: 0.22, delay: Math.min(idx, 8) * 0.04 } }}
                  >
                    <SortableBlockRow
                      block={block}
                      idx={idx}
                      blocksLength={blocks.length}
                      isPreview={isPreview}
                      isHighlighted={highlightedBlockId === block.id}
                      recallMark={recallMark?.(block.id) ?? null}
                      isMain={isMain}
                      showNameBadge={showBlockNameBadge}
                      linkTags={linkTags}
                      onOpenLink={refScope?.openUsage}
                      onMoveUp={() => moveBlock(block.id, 'up')}
                      onMoveDown={() => moveBlock(block.id, 'down')}
                      pasteAfter={clipboardLabel && onPlaceAt
                        ? {
                          label: t('task.paste.afterBlock', { label: block.name ?? String(idx + 1) }),
                          onPaste: () => { setOpenGap(null); onPlaceAt(idx); },
                          open: openGap === idx,
                          onOpen: () => setOpenGap(idx),
                          onClose: () => setOpenGap(null),
                        }
                        : undefined}
                      upRef={(el) => { if (el) upButtonRefs.current.set(block.id, el); else upButtonRefs.current.delete(block.id); }}
                      downRef={(el) => { if (el) downButtonRefs.current.set(block.id, el); else downButtonRefs.current.delete(block.id); }}
                    >
                      {/* How `FiredFeedback` knows which block it belongs to, so all eleven
                          report their states without any of them gaining a prop. Mirrors the
                          `id="block-..."` above, which `readBlockResults` reads for the same
                          reason. */}
                      <BlockIdProvider blockId={block.id} taskId={taskId}>{renderBlock(block, isMain)}</BlockIdProvider>
                    </SortableBlockRow>
                  </motion.div>
                  );
                })}
              </AnimatePresence>
            </SortableContext>

            {!isPreview && (
              <QuickAddBar
                className='mt-3'
                types={getQuickAddTypes(blocks)}
                short={isMobilePortrait}
                highlight={isLibraryDragging}
                onAdd={(type) => {
                  onDropBlock(type);
                  setAnnouncement(t('task.a11y.quickAdded', { label: t(BLOCK_META[type]!.labelKey) }));
                }}
              >
                {isMobilePortrait && (
                  <>
                    <div aria-hidden='true' className='mx-1 w-px self-stretch bg-ws-border' />
                    <button
                      onClick={onShowLibrary}
                      aria-label={t('task.quickAdd.openLibraryLabel')}
                      className='flex flex-col items-center gap-1 rounded-lg px-3 py-2 text-ws-muted transition-colors hover:bg-ws-hover hover:text-ws-primary'
                    >
                      <BlocksIcon aria-hidden='true' className='h-4 w-4' />
                      <span className='text-xs'>{t('task.quickAdd.more')}</span>
                    </button>
                  </>
                )}
              </QuickAddBar>
            )}
          </div>
        )}

        </div>
      </div>

    </div>
  );
}
